import { requestMeasure, requestMutation } from '../../lib/fasterdom/fasterdom';
import { APP_WAKE_EVENT, IS_SAFARI } from './windowEnvironment';

const WAKE_CHECK_INTERVAL_MS = 1000;
const WAKE_GAP_THRESHOLD_MS = 5000;
const WAKE_DEBOUNCE_MS = 1000;

// After Safari discards and restores a tab — its "This webpage was reloaded because it was using
// significant energy" flow, or a bfcache page restore — the app can come back with a stale
// composite: the DOM geometry is correct (the header sits at the top) but WebKit paints the whole
// app shifted up under the browser tab bar, and a normal reload does not clear it. Resizing the
// window fixes it instantly because it forces a re-layout + recomposite. Reproduce that by nudging
// the mount root's height by 1px for a single frame, which is imperceptible but forces WebKit to
// recompose the app at the correct position.
function forceRecomposite() {
  const root = document.getElementById('root');
  if (!root) return;

  requestMutation(() => {
    root.style.height = 'calc(100% - 1px)';

    requestMeasure(() => {
      // Force the shrunk height to lay out before restoring, so WebKit actually recomposites
      root.getBoundingClientRect();

      requestMutation(() => {
        root.style.height = '';
      });
    });
  });
}

function handleSafariRestore() {
  // Safari may restore the page without emitting a native resize event, leaving layout caches stale
  window.dispatchEvent(new Event(APP_WAKE_EVENT));
  window.dispatchEvent(new Event('resize'));
  forceRecomposite();
}

// Registers the recomposite triggers. Safari-only — Chromium does not exhibit the desync and would
// just pay for a wasted reflow.
export default function setupSafariRecompositeOnRestore() {
  if (!IS_SAFARI) return;

  let lastTickAt = Date.now();
  let lastWakeAt = 0;

  window.setInterval(() => {
    const now = Date.now();
    const elapsed = now - lastTickAt;
    lastTickAt = now;

    if (
      document.visibilityState !== 'visible' ||
      elapsed < WAKE_GAP_THRESHOLD_MS
    ) {
      return;
    }
    if (now - lastWakeAt < WAKE_DEBOUNCE_MS) return;

    lastWakeAt = now;
    handleSafariRestore();
  }, WAKE_CHECK_INTERVAL_MS);

  // Fires on initial load and, crucially, when Safari restores the page after discarding the tab
  window.addEventListener('pageshow', handleSafariRestore);
  // The tab can also be restored without a reload; recomposite when it becomes visible again
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      handleSafariRestore();
    }
  });
}
